import { useMemo, useEffect, useState } from "react";
import { Badge, Tabs } from "antd";

import { TaxBoardUser } from "../../../../../api/graphql/schema.generated";
import TaxCaseActions from "./TaxCaseActions";
import TaxCaseOverview from "./TaxCaseOverview";
import TaxCaseComments from "./TaxCaseComments";
import { KontaxUserContext } from "../../../../contexts/KontaxUserContext";
import TaxCaseExternal from "./TaxCaseExternal";
import colors from "../../../../../themes/colors";
import ExternalBookkeepingTab from "../../../Mandanten/ActivationView/UserDetails/ExternalBookkeepingTab";
import UserExternalBookkeepingBlock from "./UserExternalBookkeepingBlock";
import { UserFibuFinalCheck } from "../../../../common/FibuFinalCheck";
import BusinessQuestionnaire from "../../../../common/BusinessQuestionnaire";
import { useFetchDocumentCountLazyQuery } from "../../../../../api/graphql/queries/documents/documentCount.generated";
import { showGraphQlErrorNotification } from "../../../../../utils";
import { UserPages } from "../../../Mandanten/Common/UserDetails/enums";
import BankStatementsTab from "./BankStatementsTab";

const { TabPane } = Tabs;

enum TaxCaseDetailsTab {
  DASHBOARD = "DASHBOARD",
  EXTERNAL_DOCUMENTS = "EXTERNAL_DOCUMENTS",
  BANK_STATEMENTS = "BANK_STATEMENTS",
  COMMENTS = "COMMENTS",
}

const TaxCaseDetails = ({
  email,
  taxBoardUser,
  taxYear,
  onRefresh,
}: {
  email: string;
  taxBoardUser: TaxBoardUser;
  taxYear: number;
  onRefresh: () => void;
}) => {
  const declarationDocumentsCount = useMemo(() => {
    const {
      euerDeclarations,
      vatAnnualDeclarations,
      tradeTaxDeclarations,
      incomeTaxDeclarations,
    } = taxBoardUser;

    return [
      euerDeclarations[0],
      vatAnnualDeclarations[0],
      tradeTaxDeclarations[0],
      incomeTaxDeclarations[0],
    ]
      .flatMap((declaration) => declaration.declarationDocuments)
      .flatMap((document) => document.assets).length;
  }, [taxBoardUser]);

  const [
    externalBookkeepingDocumentsCount,
    setExternalBookkeepingDocumentsCount,
  ] = useState<number | null>(null);

  const [bankStatementsDocumentsCount, setBankStatementsDocumentsCount] =
    useState<number | null>(null);

  const [fetchExternalBookkeepingDocumentCount] =
    useFetchDocumentCountLazyQuery({
      variables: {
        userId: taxBoardUser.id,
        year: taxYear,
        categoryNames: [
          "EXTERNAL_BOOKEEPING_MANUAL",
          "EXTERNAL_BOOKKEEPING_ADVISOR",
          "EXTERNAL_BOOKKEEPING_TOOL",
        ],
      },
      onCompleted: (data) => {
        setExternalBookkeepingDocumentsCount(data.documentCount.count);
      },
      onError: (error) => {
        showGraphQlErrorNotification(``, error);
      },
    });

  const [fetchBankStatementsDocumentCount] = useFetchDocumentCountLazyQuery({
    variables: {
      userId: taxBoardUser.id,
      year: taxYear,
      categoryNames: ["BANK_STATEMENTS"],
    },
    onCompleted: (data) => {
      setBankStatementsDocumentsCount(data.documentCount.count);
    },
    onError: (error) => {
      showGraphQlErrorNotification(``, error);
    },
  });

  useEffect(() => {
    if (externalBookkeepingDocumentsCount === null) {
      fetchExternalBookkeepingDocumentCount();
    }
  }, [
    taxBoardUser,
    fetchExternalBookkeepingDocumentCount,
    externalBookkeepingDocumentsCount,
  ]);

  useEffect(() => {
    if (taxYear >= 2025 && bankStatementsDocumentsCount === null) {
      fetchBankStatementsDocumentCount();
    }
  }, [
    taxBoardUser,
    taxYear,
    fetchBankStatementsDocumentCount,
    bankStatementsDocumentsCount,
  ]);

  return (
    <KontaxUserContext email={email}>
      <Tabs centered style={{ width: 1000 }}>
        <TabPane tab="Dashboard" key={TaxCaseDetailsTab.DASHBOARD}>
          <TaxCaseActions taxBoardUser={taxBoardUser} onRefresh={onRefresh} />
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <UserFibuFinalCheck year={taxYear} />
            </div>
            {taxBoardUser.kontaxUser?.taxCases[0] && (
              <div style={{ flex: 1 }}>
                <BusinessQuestionnaire
                  taxCase={taxBoardUser.kontaxUser.taxCases[0]}
                  taxYear={taxYear}
                  onRefresh={onRefresh}
                />
              </div>
            )}
          </div>
          <UserExternalBookkeepingBlock />
          <TaxCaseOverview taxBoardUser={taxBoardUser} taxYear={taxYear} />
        </TabPane>
        <TabPane
          tab={
            <>
              Externe Anlagen
              <Badge
                count={declarationDocumentsCount}
                style={{ backgroundColor: colors.purple, marginLeft: 4 }}
              />
            </>
          }
          key={TaxCaseDetailsTab.EXTERNAL_DOCUMENTS}
        >
          <TaxCaseExternal taxBoardUser={taxBoardUser} onRefresh={onRefresh} />
        </TabPane>
        <TabPane
          key={UserPages.EXTERNAL_BOOKKEEPING}
          tab={
            <span id="ExterneBuchhaltungTabButton">
              Externe Buchhaltung
              {!!externalBookkeepingDocumentsCount && (
                <Badge
                  count={externalBookkeepingDocumentsCount}
                  style={{ backgroundColor: colors.purple, marginLeft: 4 }}
                />
              )}
            </span>
          }
        >
          <ExternalBookkeepingTab
            setDocumentsCount={setExternalBookkeepingDocumentsCount}
          />
        </TabPane>
        {taxYear >= 2025 && (
          <TabPane
            tab={
              <>
                Kontoauszüge
                {!!bankStatementsDocumentsCount && (
                  <Badge
                    count={bankStatementsDocumentsCount}
                    style={{ backgroundColor: colors.purple, marginLeft: 4 }}
                  />
                )}
              </>
            }
            key={TaxCaseDetailsTab.BANK_STATEMENTS}
          >
            <BankStatementsTab
              setDocumentsCount={setBankStatementsDocumentsCount}
              year={taxYear}
            />
          </TabPane>
        )}
        <TabPane tab="Kommentare" key={TaxCaseDetailsTab.COMMENTS}>
          <TaxCaseComments taxYear={taxYear} />
        </TabPane>
      </Tabs>
    </KontaxUserContext>
  );
};

export default TaxCaseDetails;
